<script setup lang="ts">
/**
 * Страница одного шаблона.
 *
 * Отдельная страница на шаблон — не украшение каталога, а его смысл. Название
 * шаблона и есть поисковый запрос: «шаблон коммерческого предложения на
 * услуги», «презентация для инвестора». Одна страница каталога на всех
 * отвечала бы на них хуже, чем страница про сам шаблон.
 *
 * Показывается настоящая сборка во фрейме и во всю ширину: до регистрации
 * человек должен увидеть то, что получит, а не описание того, что получит.
 * Текст рядом обязателен — содержимое фрейма в индекс страницы не попадает.
 */
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import SiteFooter from '../../components/marketing/SiteFooter.vue';
import SiteHeader from '../../components/marketing/SiteHeader.vue';
import { CLUSTERS } from '../../site/clusters.js';
import { templates } from '../../site/public-data.js';
import TaskField from './TaskField.vue';
import './landing.css';

const route = useRoute();

const slug = computed(() => String(route.params['slug'] ?? ''));
const template = computed(() => templates.value.find((item) => item.slug === slug.value) ?? null);

/** Соседи того же типа: человек, выбирающий макет, смотрит несколько. */
const siblings = computed(() =>
  templates.value
    .filter((item) => item.slug !== slug.value && item.docType === template.value?.docType)
    .slice(0, 6),
);

const nav = computed(() => [
  { href: '/templates', label: 'Шаблоны' },
  ...CLUSTERS.slice(0, 3).map((cluster) => ({
    href: `/${cluster.slug}`,
    label: cluster.navLabel,
  })),
  { href: '/#pricing', label: 'Цены' },
]);
</script>

<template>
  <div class="lp">
    <SiteHeader :nav="nav" />

    <section class="lp-wrap lp-section">
      <!--
        Хлебные крошки настоящие, а не нарисованные: ссылка вверх нужна и
        человеку, и поиску — он по ней и понимает, что это раздел сайта.
      -->
      <p class="lp-eyebrow">
        <RouterLink to="/templates">Шаблоны</RouterLink>
        <template v-if="template?.docType"> · {{ template.docType }}</template>
      </p>

      <template v-if="template">
        <h1 class="lp-h1">{{ template.name }}</h1>
        <p v-if="template.description" class="lp-lede">{{ template.description }}</p>

        <p class="lp-note">
          Это готовый макет. Опишите свою задачу — нейросеть переделает его под вас: заменит
          текст, подставит ваши цвета, уберёт лишние блоки и добавит нужные. Час на документ,
          правки словами.
        </p>

        <!--
          Шаблон передаётся дальше: человек нажал начать на его странице, и
          документ соберётся именно с него, а не с заготовки.
        -->
        <TaskField :template-slug="template.slug" />
      </template>

      <template v-else>
        <h1 class="lp-h1">Шаблон не найден</h1>
        <p class="lp-lede">
          Возможно, его убрали из каталога. Посмотрите
          <RouterLink to="/templates">остальные шаблоны</RouterLink> — или опишите задачу словами
          на <RouterLink to="/">главной</RouterLink>, и макет подберётся сам.
        </p>
      </template>
    </section>

    <section v-if="template?.versionId" class="lp-wrap lp-section">
      <div class="tpl-frame">
        <iframe :src="`/preview/template/${template.versionId}`" :title="template.name"></iframe>
      </div>
      <p class="lp-note tpl-frame-note">
        Так шаблон выглядит целиком. Это настоящая сборка, а не картинка: страница внутри живая.
      </p>
    </section>

    <section v-if="siblings.length > 0" class="lp-wrap lp-section">
      <h2 class="lp-h2">Похожие шаблоны</h2>
      <ul class="tpl-siblings">
        <li v-for="item in siblings" :key="item.slug">
          <RouterLink :to="`/templates/${item.slug}`">{{ item.name }}</RouterLink>
        </li>
      </ul>
    </section>

    <SiteFooter />
  </div>
</template>

<style scoped>
/*
 * Фрейм во всю ширину и высокий: шаблон — это документ, и смотрят его целиком,
 * прокручивая. Уменьшать его здесь, как в карточке каталога, значит показать
 * то же самое, только мельче.
 */
.tpl-frame {
  border: 1px solid rgb(229 231 235);
  border-radius: 0.75rem;
  overflow: hidden;
  background: #fff;
}

.tpl-frame iframe {
  display: block;
  width: 100%;
  height: min(80vh, 900px);
  border: 0;
}

.tpl-frame-note {
  margin-top: 0.75rem;
}

.tpl-siblings {
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem 1rem;
  list-style: none;
  margin: 1rem 0 0;
  padding: 0;
}
</style>
